import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { Feed, FeedError } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { formatDate } from "../format.js";
import { navigate } from "../router.js";
import { sharedStyles } from "../styles.js";
import "../components/add-feed-dialog.js";
import "../components/favicon.js";

type FeedFilter = "all" | "healthy" | "errored" | "dead";

/** Feed library: subscribe by URL, see health and failure logs, unsubscribe. */
export class NfFeedsView extends LitElement {
  static override properties = {
    api: { attribute: false },
    feeds: { state: true },
    loading: { state: true },
    error: { state: true },
    filter: { state: true },
    query: { state: true },
    errorsByFeed: { state: true },
    openErrorsId: { state: true },
    errorsLoadingId: { state: true },
    logsError: { state: true },
  };

  api!: ApiClient;
  feeds: Feed[] = [];
  loading = false;
  error = "";
  filter: FeedFilter = "all";
  query = "";
  errorsByFeed: Record<string, FeedError[]> = {};
  openErrorsId: string | null = null;
  errorsLoadingId: string | null = null;
  logsError = "";

  static override styles = [
    sharedStyles,
    css`
      .add-row {
        margin-bottom: var(--nf-space-16);
      }
      .filter-input {
        width: 100%;
        margin-bottom: var(--nf-space-12);
      }
      .filters {
        display: flex;
        gap: var(--nf-space-8);
        flex-wrap: wrap;
        margin-bottom: var(--nf-space-12);
      }
      .chip.active {
        background: var(--nf-accent);
        color: var(--nf-accent-fg);
        border-color: var(--nf-accent);
      }
      .feed {
        display: flex;
        gap: var(--nf-space-12);
        align-items: flex-start;
        padding: var(--nf-space-12);
        border-bottom: 1px solid var(--nf-border);
      }
      .feed:last-child {
        border-bottom: none;
      }
      .main {
        flex: 1;
        min-width: 0;
      }
      .feed__icon {
        margin-top: var(--nf-space-2);
        --nf-favicon-size: 20px;
      }
      .name {
        font-weight: 600;
        background: none;
        border: none;
        padding: 0;
        color: var(--nf-accent);
        cursor: pointer;
        font-size: var(--nf-font-15);
      }
      .name:hover {
        text-decoration: underline;
      }
      .url {
        font-size: var(--nf-font-13);
        word-break: break-all;
      }
      .health {
        font-size: var(--nf-font-13);
        margin-top: var(--nf-space-4);
        display: flex;
        gap: var(--nf-space-8);
        align-items: center;
        flex-wrap: wrap;
      }
      .badge.dead {
        background: var(--nf-danger);
        color: var(--nf-accent-fg);
      }
      .badge.errored {
        background: var(--nf-warning);
        color: var(--nf-accent-fg);
      }
      .badge.ok {
        background: var(--nf-success);
        color: var(--nf-accent-fg);
      }
      .badge.fetch {
        background: var(--nf-warning);
        color: var(--nf-accent-fg);
      }
      .badge.parse {
        background: var(--nf-accent);
        color: var(--nf-accent-fg);
      }
      .badge.ingest {
        background: var(--nf-danger);
        color: var(--nf-accent-fg);
      }
      .log {
        margin-top: var(--nf-space-8);
        background: var(--nf-surface-2);
        border-radius: var(--nf-radius);
        padding: 0 var(--nf-space-8);
      }
      .log__row {
        display: flex;
        gap: var(--nf-space-8);
        align-items: baseline;
        flex-wrap: wrap;
        font-size: var(--nf-font-13);
        padding: var(--nf-space-6) 0;
        border-bottom: 1px solid var(--nf-border);
      }
      .log__row:last-child {
        border-bottom: none;
      }
      .log__msg {
        flex: 1 1 100%;
        word-break: break-word;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: var(--nf-font-13);
      }
    `,
  ];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api")) void this.load();
  }

  private async load(): Promise<void> {
    if (!this.api) return;
    this.loading = true;
    this.error = "";
    try {
      this.feeds = await this.api.listFeeds();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this.loading = false;
    }
  }

  private openAddDialog(): void {
    const dialog = this.shadowRoot?.querySelector("nf-add-feed-dialog");
    void dialog?.openDialog();
  }

  private async unsubscribe(feed: Feed): Promise<void> {
    const label = feed.title ?? feed.url;
    if (
      !window.confirm(
        `Unsubscribe from "${label}"? Non-kept articles will be removed.`,
      )
    )
      return;
    try {
      await this.api.unsubscribe(feed.id);
      await this.load();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    }
  }

  private healthBadge(feed: Feed): TemplateResult {
    if (feed.appearsDead)
      return html`<span class="badge dead">Appears dead</span>`;
    if (feed.isErrored) return html`<span class="badge errored">Errors</span>`;
    if (feed.lastFetchedAt === null)
      return html`<span class="badge fetch">Fetching…</span>`;
    return html`<span class="badge ok">Healthy</span>`;
  }

  private filteredFeeds(): Feed[] {
    const byStatus = this.feeds.filter((feed) => {
      switch (this.filter) {
        case "healthy":
          return !feed.isErrored;
        case "errored":
          return feed.isErrored && !feed.appearsDead;
        case "dead":
          return feed.appearsDead;
        default:
          return true;
      }
    });
    const query = this.query.trim().toLowerCase();
    if (query === "") return byStatus;
    return byStatus.filter(
      (feed) =>
        (feed.title ?? "").toLowerCase().includes(query) ||
        feed.url.toLowerCase().includes(query),
    );
  }

  private counts(): Record<FeedFilter, number> {
    return {
      all: this.feeds.length,
      healthy: this.feeds.filter((f) => !f.isErrored).length,
      errored: this.feeds.filter((f) => f.isErrored && !f.appearsDead).length,
      dead: this.feeds.filter((f) => f.appearsDead).length,
    };
  }

  private selectFilter(filter: FeedFilter): void {
    this.filter = filter;
  }

  async toggleErrors(feed: Feed): Promise<void> {
    if (this.openErrorsId === feed.id) {
      this.openErrorsId = null;
      return;
    }
    this.openErrorsId = feed.id;
    this.logsError = "";
    if (this.errorsByFeed[feed.id] === undefined) {
      this.errorsLoadingId = feed.id;
      try {
        this.errorsByFeed = {
          ...this.errorsByFeed,
          [feed.id]: await this.api.listFeedErrors(feed.id),
        };
      } catch (err) {
        this.logsError = err instanceof ApiError ? err.message : String(err);
      } finally {
        this.errorsLoadingId = null;
      }
    }
  }

  private filterButton(value: FeedFilter, label: string): TemplateResult {
    return html`
      <button
        class="chip ${this.filter === value ? "active" : ""}"
        @click=${() => this.selectFilter(value)}
      >
        ${label} (${this.counts()[value]})
      </button>
    `;
  }

  private errorsPanel(feed: Feed): TemplateResult {
    const errors = this.errorsByFeed[feed.id];
    if (this.errorsLoadingId === feed.id) {
      return html`<div class="log muted">Loading…</div>`;
    }
    if (!errors) return html``;
    if (errors.length === 0) {
      return html`<div class="log muted">No logged failures.</div>`;
    }
    return html`
      <div class="log">
        ${errors.map(
          (e) => html`
            <div class="log__row">
              <span class="muted">${formatDate(e.occurredAt)}</span>
              <span class="badge ${e.kind}">${e.kind}</span>
              ${
                e.status !== null
                  ? html`<span class="muted">HTTP ${e.status}</span>`
                  : nothing
              }
              <span class="log__msg">${e.message}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  override render(): TemplateResult {
    const visible = this.filteredFeeds();
    return html`
      <div class="add-row">
        <button class="primary" @click=${this.openAddDialog}>Add</button>
      </div>
      <nf-add-feed-dialog
        .api=${this.api}
        .feeds=${this.feeds}
        @nf-feed-subscribed=${() => void this.load()}
      ></nf-add-feed-dialog>
      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      <input
        class="filter-input"
        type="search"
        placeholder="Filter by name or URL"
        aria-label="Filter feeds"
        .value=${this.query}
        @input=${(e: Event) => {
          if (e.target instanceof HTMLInputElement) this.query = e.target.value;
        }}
      />
      <div class="filters" role="group" aria-label="Filter feeds by status">
        ${this.filterButton("all", "All")}
        ${this.filterButton("healthy", "Healthy")}
        ${this.filterButton("errored", "Errors")}
        ${this.filterButton("dead", "Appears dead")}
      </div>
      ${
        this.loading
          ? html`<div class="empty">Loading…</div>`
          : this.feeds.length === 0
            ? html`<div class="empty">No subscriptions yet.</div>`
            : visible.length === 0
              ? html`<div class="empty">No feeds match this filter.</div>`
              : html`<div class="card" style="padding:0">
                ${visible.map(
                  (feed) =>
                    html`<div class="feed">
                    <nf-favicon
                      class="feed__icon"
                      .url=${feed.faviconUrl}
                      .label=${feed.title ?? feed.url}
                    ></nf-favicon>
                    <div class="main">
                      <button
                        class="name"
                        @click=${() => navigate(`/feed/${feed.id}`)}
                      >
                        ${feed.title ?? feed.url}
                      </button>
                      <div class="url muted">${feed.url}</div>
                      <div class="health">
                        ${this.healthBadge(feed)}
                        <span class="muted">errors: ${feed.errorCount}</span>
                        <span class="muted"
                          >last fetched: ${formatDate(feed.lastFetchedAt)}</span
                        >
                        <button
                          class="chip"
                          @click=${() => void this.toggleErrors(feed)}
                        >
                          ${this.openErrorsId === feed.id ? "Hide log" : "Log"}
                        </button>
                      </div>
                      ${
                        this.openErrorsId === feed.id
                          ? this.errorsPanel(feed)
                          : nothing
                      }
                    </div>
                    <button
                      class="danger"
                      @click=${() => void this.unsubscribe(feed)}
                    >
                      Unsubscribe
                    </button>
                  </div>`,
                )}
              </div>`
      }
      ${this.logsError ? html`<div class="card error">${this.logsError}</div>` : nothing}
    `;
  }
}

customElements.define("nf-feeds-view", NfFeedsView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-feeds-view": NfFeedsView;
  }
}
